import sty from "@/wab/client/components/sidebar-tabs/DataBinding/DataPickerColumn.module.scss";
import DataPickerColumnItem from "@/wab/client/components/sidebar-tabs/DataBinding/DataPickerColumnItem";
import {
  ColumnItem,
  DataPickerOpts,
  evalExpr,
  getDollarVarIcon,
  getItemPath,
  getSourceUiId,
  getSupportedObjectKeys,
  getVariableType,
  isListType,
  isTypeSupported,
} from "@/wab/client/components/sidebar-tabs/DataBinding/DataPickerUtil";
import { useViewCtxMaybe } from "@/wab/client/contexts/StudioContexts";
import {
  DefaultDataPickerColumnProps,
  PlasmicDataPickerColumn,
} from "@/wab/client/plasmic/plasmic_kit_data_binding/PlasmicDataPickerColumn";
import { BLOCKED_RUN_INTERACTION_ONLY_BY_EVENT_ARGS_MESSAGE } from "@/wab/client/state-management/interactions-meta";
import {
  canRunInteraction,
  runInteraction,
} from "@/wab/client/state-management/preview-steps";
import { useStudioCtx } from "@/wab/client/studio-ctx/StudioCtx";
import { ensure, isTruthy } from "@/wab/shared/common";
import { isTplTagOrComponent } from "@/wab/shared/core/tpls";
import { DEVFLAGS } from "@/wab/shared/devflags";
import { pathToString } from "@/wab/shared/eval/expression-parser";
import { ensureKnownInteraction } from "@/wab/shared/model/classes";
import { mkMetaName } from "@plasmicapp/host";
import { HTMLElementRefOf } from "@plasmicapp/react-web";
import { notification } from "antd";
import { observer } from "mobx-react";
import * as React from "react";

export interface DataPickerColumnProps extends DefaultDataPickerColumnProps {
  data: Record<string, any>;
  columnIndex: number;
  selectedItem: number | undefined;
  isActiveColumn: boolean;
  columnItems: ColumnItem[];
  onItemSelected: (column: number, item: number) => void;
  opts: DataPickerOpts;
  onCancelDataPicker?: () => void;
  // Error message shown in place of data fields when set.
  errorMessage?: string;
}

/** The contents of a column standing in for data that failed to resolve. */
function ColumnErrorNotice(props: { columnIndex: number; message: string }) {
  const { columnIndex, message } = props;
  return (
    <div
      className={sty.columnError}
      data-test-id={`data-picker-column-${columnIndex}-error`}
    >
      <div className={sty.columnErrorTitle}>Error</div>
      <div className={sty.columnErrorMessage}>{message}</div>
    </div>
  );
}

function DataPickerColumn_(
  props: DataPickerColumnProps,
  ref: HTMLElementRefOf<"div">
) {
  const {
    data,
    columnIndex,
    selectedItem,
    columnItems,
    onItemSelected,
    isActiveColumn,
    opts,
    onCancelDataPicker,
    errorMessage,
    ...rest
  } = props;
  if (!data) {
    return <PlasmicDataPickerColumn root={{ ref }} children={undefined} />;
  }

  const studioCtx = useStudioCtx();
  const viewCtx = useViewCtxMaybe();
  const getInteraction = React.useCallback(
    (key: string) => {
      return data["$steps"]?.[mkMetaName(key)]?.interaction;
    },
    [data, studioCtx]
  );
  const wasStepExecuted = React.useCallback(
    (key: string) => {
      const interaction = getInteraction(key);
      return interaction && studioCtx.hasCached$stepValue(interaction.uuid);
    },
    [data, studioCtx, getInteraction]
  );
  const isStepsColumn = React.useMemo(() => {
    const pathPrefix = columnItems.find((item) => item.pathPrefix)?.pathPrefix;
    return pathPrefix && pathPrefix.length === 1 && pathPrefix[0] === "$steps";
  }, [columnItems]);
  const enabledPreviewSteps = DEVFLAGS.previewSteps;

  return (
    <PlasmicDataPickerColumn
      root={{ ref, id: `data-picker-column-${columnIndex}` }}
      {...rest}
      previewSteps={enabledPreviewSteps && isStepsColumn}
      children={
        errorMessage != null ? (
          <ColumnErrorNotice columnIndex={columnIndex} message={errorMessage} />
        ) : (
          columnItems
            .map((item, index) => {
              const { name: key, label, value } = item;
              const path = getItemPath(item);
              const variableType = getVariableType(value);
              if (!isTypeSupported(variableType)) {
                return undefined;
              }
              const keyCount = isListType(variableType)
                ? getSupportedObjectKeys(value, opts).length
                : 0;
              // Indicator for a row whose data errored.
              const errorPreview =
                item.errorMessage != null ? "error" : undefined;
              const previewValue =
                errorPreview ??
                (!isListType(variableType)
                  ? evalExpr(
                      path,
                      ensure(data, "Should only be called if data exists")
                    )
                  : variableType === "array"
                  ? keyCount + ` item${keyCount === 1 ? "" : "s"}`
                  : undefined);
              const isSelected = index === selectedItem;
              const sourceUiId = viewCtx
                ? getSourceUiId(path, viewCtx.site, viewCtx.currentComponent())
                : undefined;
              return (
                <DataPickerColumnItem
                  ref={
                    isSelected && isActiveColumn
                      ? (el) =>
                          setTimeout(
                            () =>
                              el?.scrollIntoView({
                                block: "center",
                                inline: "start",
                                behavior: "smooth",
                              }),
                            0
                          )
                      : undefined
                  }
                  key={pathToString(path)}
                  itemName={label ?? key}
                  variableType={variableType}
                  icon={columnIndex === 0 ? getDollarVarIcon(path) : undefined}
                  previewValue={previewValue}
                  isSelected={isSelected}
                  onClick={() => onItemSelected(columnIndex, index)}
                  onJumpToSource={
                    sourceUiId
                      ? () => {
                          onCancelDataPicker?.();
                          studioCtx.uiActionBus.dispatch(sourceUiId, "jump");
                        }
                      : undefined
                  }
                  columnIndex={columnIndex}
                  step={
                    isStepsColumn && enabledPreviewSteps
                      ? wasStepExecuted(key)
                        ? "played"
                        : "notPlayed"
                      : undefined
                  }
                  interaction={getInteraction(key)}
                />
              );
            })
            .filter(isTruthy)
        )
      }
      runAllSteps={{
        onClick: () => {
          const currentInteraction = ensureKnownInteraction(
            data[mkMetaName("$steps")]?.currentInteraction
          );
          const previousSteps = currentInteraction.parent.interactions.slice(
            0,
            currentInteraction.parent.interactions.indexOf(currentInteraction)
          );
          const vc = studioCtx.focusedViewCtx();
          const tpl = vc?.focusedTpls()?.[0];
          if (!vc || !tpl || !isTplTagOrComponent(tpl)) {
            return;
          }
          for (const interaction of previousSteps) {
            if (!canRunInteraction(interaction, vc, { skipSteps: true })) {
              notification.error({
                message: BLOCKED_RUN_INTERACTION_ONLY_BY_EVENT_ARGS_MESSAGE,
              });
              return;
            }
          }
          for (const interaction of previousSteps) {
            runInteraction(interaction, vc, tpl);
          }
        },
      }}
    />
  );
}

const DataPickerColumn = observer(React.forwardRef(DataPickerColumn_));
export default DataPickerColumn;
